import React, {useMemo} from 'react';
import DashboardLayout from "../../layouts/DashboardLayout";
import {ArrowDownIcon, ArrowUpIcon, ChartBarIcon} from '@heroicons/react/20/solid'
import {CreditCardIcon, DocumentDuplicateIcon, UsersIcon, CurrencyDollarIcon} from '@heroicons/react/24/outline'
import {clsx} from "clsx";
import {apollo} from "../../utils/apollo";
import {useQuery} from "react-query";
import {GET_MERCHANT_STATISTICS, GET_MERCHANT_SUPPLIERS_STATISTICS, GET_PAYMENT_LAST_MONTH_CHART, GET_TRANSACTION_STATUS_TODAY_BY_HOUR} from "../../utils/graphql";
import useAuthStore from "../../store/auth";
import {formatNumberFloat, formatNumberInt} from "../../utils/helpers";
import Loading from "../../components/Loading";
import {useTranslation} from "react-i18next";
import PaymentLastMonthChart from "./components/PaymentLastMonthChart";
import TransactionStatusByHourChart from "./components/TransactionStatusByHourChart";

// const stats = [
// 	{id: 1, name: 'Number of transactions', stat: '71,897', icon: DocumentDuplicateIcon, change: '122', changeType: 'increase'},
// 	{id: 2, name: 'Successful payments', stat: '58.16%', icon: CreditCardIcon, change: '5.4%', changeType: 'increase'},
// 	{id: 3, name: 'New customers', stat: '34', icon: UsersIcon, change: '12', changeType: 'decrease'},
// ]
const icons = {
    DocumentDuplicateIcon,
    CreditCardIcon,
    UsersIcon
};

function Dashboard() {
    const {t} = useTranslation();
    const {merchant} = useAuthStore();

    const fetchMerchantStatistics = useQuery({
        queryKey: 'dashboard_statistics',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_STATISTICS,
                variables: {},
            });
            return data?.getMerchantStatistics;
        },
        keepPreviousData: false
    });

    const fetchMerchantSuppliersStatistics = useQuery({
        queryKey: 'dashboard_suppliers_statistics',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_STATISTICS,
                variables: {},
            });
            return data?.getMerchantSuppliersStatistics;
        },
        keepPreviousData: false,
        enabled: merchant?.allowSubVendors === true
    });

    const stats = fetchMerchantStatistics?.isSuccess ? fetchMerchantStatistics?.data : null;
    const suppliersStats = fetchMerchantSuppliersStatistics?.isSuccess ? fetchMerchantSuppliersStatistics?.data : null;

    // Charts data
    const fetchPaymentLastMonth = useQuery({
        queryKey: 'dashboard_payment_last_month',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_PAYMENT_LAST_MONTH_CHART,
                variables: {},
            });
            return data?.getPaymentLastMonth || [];
        },
        keepPreviousData: false
    });

    const fetchTransactionStatusTodayByHour = useQuery({
        queryKey: 'dashboard_transaction_status_today_by_hour',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_TRANSACTION_STATUS_TODAY_BY_HOUR,
                variables: {},
            });
            return data?.getTransactionStatusTodayByHour || [];
        },
        keepPreviousData: false
    });

    const paymentLastMonthChartData = useMemo(() => {
        if (!fetchPaymentLastMonth?.data?.length) return [];
        return fetchPaymentLastMonth.data.map((r) => ({
            date: r?.date,
            buckets: {
                completed: r?.completed || 0,
                pending: r?.pending || 0,
                failed: r?.failed || 0,
                canceled: r?.canceled || 0,
                refunded: r?.refunded || 0,
                expired: r?.expired || 0,
                on_hold: r?.on_hold || 0,
                blocked: r?.blocked || 0,
            }
        }));
    }, [fetchPaymentLastMonth?.data]);

    const transactionStatusByHourData = useMemo(() => {
        if (!fetchTransactionStatusTodayByHour?.data?.length) return [];
        return fetchTransactionStatusTodayByHour.data.map((r) => ({
            hour: Number(r?.hour) || 0,
            pending: r?.pending || 0,
            completed: r?.completed || 0,
            failed: r?.failed || 0,
        }));
    }, [fetchTransactionStatusTodayByHour?.data]);

    return (
        <DashboardLayout>
            <div>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.overview_title')}</h2>
                    <p className="text-gray-600 mt-1">{t('dashboard.overview_description')}</p>
                </div>

                <div className="mt-5">
                    {fetchMerchantStatistics.isLoading && (
                        <div className="flex justify-center items-center py-12">
                            <Loading/>
                        </div>
                    )}

                    {fetchMerchantStatistics.isSuccess && !stats?.length && (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
                            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300" />
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('dashboard.no_statistics')}</h3>
                            <p className="mt-1 text-sm text-gray-500">{t('dashboard.statistics_empty')}</p>
                        </div>
                    )}

                    {fetchMerchantStatistics.isSuccess && !!stats?.length && (
                        <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                            {stats?.map((item) => {
                                let IconComponent = icons[item.icon] || null;
                                return <div
                                    key={item.id}
                                    className="relative overflow-hidden rounded-[2rem] bg-white p-6 shadow-sm border border-gray-100 transition-all duration-200 hover:shadow-md"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="p-3 rounded-2xl bg-[#0b6a8c]/10">
                                            {IconComponent ? (
                                                <IconComponent className="h-6 w-6 text-[#0b6a8c]" aria-hidden="true"/>
                                            ) : (
                                                <ChartBarIcon className="h-6 w-6 text-[#0b6a8c]" aria-hidden="true"/>
                                            )}
                                        </div>
                                        {(item.change && item.changeType) && (
                                            <div
                                                className={clsx(
                                                    'flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider',
                                                    item.changeType === 'increase'
                                                        ? 'bg-green-50 text-green-600 border border-green-100'
                                                        : 'bg-rose-50 text-rose-600 border border-rose-100'
                                                )}
                                            >
                                                {item.changeType === 'increase' ? (
                                                    <ArrowUpIcon className="h-3 w-3" aria-hidden="true"/>
                                                ) : (
                                                    <ArrowDownIcon className="h-3 w-3" aria-hidden="true"/>
                                                )}
                                                <span>{item.change}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-1">{item.name}</p>
                                        <p className="text-3xl font-black text-gray-900 tracking-tight">{prepareStatsValue(item)}</p>
                                    </div>
                                </div>
                            })}
                        </dl>
                    )}
                </div>

                {/* Charts Row */}
                <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div>
                        {fetchPaymentLastMonth.isLoading ? (
                            <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 h-64 flex items-center justify-center"><Loading /></div>
                        ) : (
                            <PaymentLastMonthChart data={paymentLastMonthChartData} title={t('dashboard.payment_last_month') || 'Payments last month'} />
                        )}
                    </div>
                    <div>
                        {fetchTransactionStatusTodayByHour.isLoading ? (
                            <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 h-64 flex items-center justify-center"><Loading /></div>
                        ) : (
                            <TransactionStatusByHourChart data={transactionStatusByHourData} title={t('dashboard.transaction_status_today_by_hour') || 'Transaction Status Today By Hour'} />
                        )}
                    </div>
                </div>

                {merchant?.allowSubVendors === true && (
                <div className="mt-12">
                    <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-2xl bg-[#0b6a8c]/10">
                                    <CurrencyDollarIcon className="h-6 w-6 text-[#0b6a8c]" />
                                </div>
                        								<div>
									<h3 className="text-lg font-bold text-gray-900">{t('dashboard.supplier_balances')}</h3>
									<p className="text-xs font-medium text-gray-500">{t('dashboard.supplier_balances_description')}</p>
								</div>
                            </div>
                            <span className="text-[10px] font-bold text-[#0b6a8c] bg-cyan-50 border border-cyan-100 px-3 py-1 rounded-full uppercase tracking-wider">
                                {suppliersStats?.length || 0} {t('dashboard.suppliers_count')}
                            </span>
                        </div>

                        {fetchMerchantSuppliersStatistics.isLoading && (
                            <div className="flex justify-center items-center py-12">
                                <Loading />
                            </div>
                        )}

                        {fetchMerchantSuppliersStatistics.isSuccess && !suppliersStats?.length && (
                            <div className="p-12 text-center">
                                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-200" />
                                <h3 className="mt-4 text-sm font-bold text-gray-900">{t('dashboard.no_supplier_data')}</h3>
                                <p className="mt-1 text-sm text-gray-500 font-medium">{t('dashboard.no_supplier_info')}</p>
                            </div>
                        )}

                        {fetchMerchantSuppliersStatistics.isSuccess && !!suppliersStats?.length && (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-50">
                                    <thead className="bg-gray-50/50">
                                        <tr>
                                            <th scope="col"
                                                className="px-8 py-4 ltr:text-left rtl:text-right text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                {t('dashboard.table.supplier_id')}
                                            </th>
                                            <th scope="col"
                                                className="px-8 py-4 ltr:text-left rtl:text-right text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                {t('dashboard.table.supplier_name')}
                                            </th>
                                            <th scope="col"
                                                className="px-8 py-4 ltr:text-left rtl:text-right text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                {t('dashboard.table.available_amount')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-50">
                                        {suppliersStats?.map((record) => (
                                            <tr key={record?.id} className="hover:bg-gray-50/50 transition-colors">
                                                <td className="px-8 py-5 whitespace-nowrap text-sm font-bold text-gray-900">{record?.id}</td>
                                                <td className="px-8 py-5 whitespace-nowrap text-sm font-medium text-gray-600">{record?.name}</td>
                                                <td className="px-8 py-5 whitespace-nowrap text-sm font-black text-[#0b6a8c]">{record?.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                )}

            </div>
        </DashboardLayout>
    );
}


function prepareStatsValue(item) {
    let value = item.stat;
    if (item.valueType === 'float') value = formatNumberFloat(value);
    else if (item.valueType === 'integer') value = formatNumberInt(value);
    return `${item?.prefix ? item?.prefix + ' ' : ''}${value}${item?.suffix ? ' ' + item?.suffix : ''}`;
}

export default Dashboard;
